import React from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Linkedin, 
  Twitter, 
  Facebook,
  Globe,
  ArrowUp
} from 'lucide-react';

const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gray-900 text-white" role="contentinfo">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="text-2xl font-bold text-blue-400 mb-4">
              <img src='/keops-light.png' alt="KEOPS Light Logo" className="h-8 w-auto" width="120" height="32" />
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Turn Regulatory Reporting challenges into competitive advantages through automation, expertise, and scalable solutions.
            </p>
            
            {/* Social Networks */}
            <div className="flex space-x-4">
              <a 
                href="https://www.linkedin.com/company/keops-risk-reporting/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://x.com/keops_eu" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-400 transition-colors duration-300"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Contact Information */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                <div className="text-gray-300">
                  <p className="font-medium">Luxembourg Office</p>
                  <p>12, rue Guillaume Schneider</p>
                  <p>L-2522 Luxembourg</p>
                </div>
              </div>
              
              {/* <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="text-gray-300">
                  <a href="tel:+352123456789" className="hover:text-blue-400 transition-colors duration-300">
                    +352 123 456 789
                  </a>
                </div>
              </div> */}
              
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="text-gray-300">
                  <a href="mailto:into@keops.lu" className="hover:text-blue-400 transition-colors duration-300">
                    into AT keops.lu
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="text-gray-300">
                  <a href="https://keops.lu" className="hover:text-blue-400 transition-colors duration-300">
                    keops.lu
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3 text-gray-300">
              <li>
                <a href="#product-coverage" className="hover:text-blue-400 transition-colors duration-300">
                  PRIIPs Reporting
                </a>
              </li>
              <li>
                <a href="#product-coverage" className="hover:text-blue-400 transition-colors duration-300">
                  AIFMD Compliance
                </a>
              </li>
              <li>
                <a href="#product-coverage" className="hover:text-blue-400 transition-colors duration-300">
                  Solvency II
                </a>
              </li>
              <li>
                <a href="#product-coverage" className="hover:text-blue-400 transition-colors duration-300">
                  SFDR Reporting
                </a>
              </li>
              <li>
                <a href="#product-coverage" className="hover:text-blue-400 transition-colors duration-300">
                  ESG Data Management
                </a>
              </li>
              <li>
                <a href="#product-coverage" className="hover:text-blue-400 transition-colors duration-300">
                  Risk Analytics
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3 text-gray-300">
              <li>
                <a href="#why-keops" className="hover:text-blue-400 transition-colors duration-300">
                  Why Keops
                </a>
              </li>
              <li>
                <a href="#solutions" className="hover:text-blue-400 transition-colors duration-300">
                  Solutions
                </a>
              </li>
              <li>
                <button 
                  onClick={() => (window as any).navigate?.('/privacy')}
                  className="hover:text-blue-400 transition-colors duration-300 text-left"
                >
                  Privacy Policy & Terms of Use
                </button>
              </li>
              <li>
                <button 
                  onClick={scrollToTop}
                  className="flex items-center space-x-2 hover:text-blue-400 transition-colors duration-300"
                >
                  <ArrowUp className="w-4 h-4" />
                  <span>Back to Top</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              &copy; {(new Date()).getFullYear()} KEOPS. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <button 
                onClick={() => (window as any).navigate?.('/privacy')}
                className="hover:text-blue-400 transition-colors duration-300"
              >
                Privacy Policy & Terms of Use
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
